import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  Inventory,
  SellerProductInventoriesQuery,
  UnitCode,
} from '~/graphql/graphql';
import type { SellerInventoryForBuyer } from '~/types/inventory';

type ReturnedSellerProductInventory = NonNullable<
  SellerProductInventoriesQuery['sellerProductInventories']['content'][0]
>;

type InventoryState = {
  inventory: Inventory | null;
  inventories: Inventory[] | null;
  sellerInventories: ReturnedSellerProductInventory[] | null;
  sellerInventoriesForBuyer: SellerInventoryForBuyer[] | null;
  editedTallyPriceByKey: Record<string, number>;
  editedVendorGroupPriceByKey: Record<string, EditedPrice>;
};

type EditedPrice = { price: number; unit: UnitCode };

const initialState: InventoryState = {
  inventory: null,
  sellerInventories: null,
  inventories: null,
  sellerInventoriesForBuyer: null,
  editedTallyPriceByKey: {},
  editedVendorGroupPriceByKey: {},
};

const inventorySlice = createSlice({
  name: 'inventory',
  initialState,
  reducers: {
    setInventory(state, action) {
      state.inventory = action.payload;
    },
    setSellerInventories(state, action) {
      state.sellerInventories = action.payload;
    },
    setInventories(state, action) {
      state.inventories = action.payload;
    },
    setSellerInventoriesForBuyer(state, action) {
      state.sellerInventoriesForBuyer = action.payload;
    },
    setEditedTallyPrice(
      state,
      action: PayloadAction<{ key: string; price: number }>
    ) {
      state.editedTallyPriceByKey[action.payload.key] = action.payload.price;
    },
    clearEditedTallyPrice(state, action: PayloadAction<{ key: string }>) {
      delete state.editedTallyPriceByKey[action.payload.key];
    },
    setEditedVendorGroupPrice(
      state,
      action: PayloadAction<{ key: string } & EditedPrice>
    ) {
      const { key, price, unit } = action.payload;
      state.editedVendorGroupPriceByKey[key] = { price, unit };
    },
    clearEditedVendorGroupPrice(state, action: PayloadAction<{ key: string }>) {
      delete state.editedVendorGroupPriceByKey[action.payload.key];
    },
    discardEditedVendorGroupPricesNotInUnit(
      state,
      action: PayloadAction<UnitCode>
    ) {
      state.editedVendorGroupPriceByKey = Object.fromEntries(
        Object.entries(state.editedVendorGroupPriceByKey).filter(
          ([, edited]) => edited.unit === action.payload
        )
      );
    },
  },
});

export const {
  setInventory,
  setSellerInventories,
  setInventories,
  setSellerInventoriesForBuyer,
  setEditedTallyPrice,
  clearEditedTallyPrice,
  setEditedVendorGroupPrice,
  clearEditedVendorGroupPrice,
  discardEditedVendorGroupPricesNotInUnit,
} = inventorySlice.actions;
export default inventorySlice.reducer;

/**
 * Edited prices entered per `unit`. An edit made per another unit is left out, so a change of
 * preferred unit falls back to the list price rather than reading the old number in the new unit.
 */
export function editedPricesInUnit(
  editedPriceByKey: Record<string, EditedPrice>,
  unit: UnitCode
): Map<string, number> {
  return new Map(
    Object.entries(editedPriceByKey)
      .filter(([, edited]) => edited.unit === unit)
      .map(([key, edited]) => [key, edited.price])
  );
}
